import { type TFunction } from 'i18next'
import { produce } from 'immer'
import * as R from 'remeda'

import {
  calcHeatmapRatio,
  type CellInfo,
  compact,
  type DataDisplayType,
  getDiffKey,
  getDiffText,
  getSortIconBySortValue,
  type Group,
  groupAllLabelI18nKey,
  type GroupAndAll,
  NumFormat,
  type RowInfo,
  type SortValue,
} from '~/utils'

import {
  type GroupsDiff,
  type GroupsDiffColumnKey,
  type RowData,
} from './common'
import {
  type IndividualColumnKey,
  type IndividualSortColumnKey,
} from './individual'

type BuildIndividualTableInfoParams<T extends RowData<string>> = {
  addHighlightProps: (key: string) => Partial<CellInfo>
  answerTextClassName: string
  dataDisplayType: DataDisplayType
  getAnswerText: (rowData: T) => string
  getRowId: (rowData: T) => string
  groupAndAllNameMap: Record<GroupAndAll, string>
  groups: GroupAndAll[]
  groupsDiffs: GroupsDiff[]
  hiddenRowIds: string[]
  onGroupNameClick: (group: string) => void
  rowsData: T[]
  sortFunc: (items: T[]) => T[]
  sortValue: SortValue<IndividualSortColumnKey> | null
  t: TFunction
  tamRatio: number
  totalData: { counts: Record<string, number>; ratios: Record<string, number> }
}

export const buildIndividualTableInfo = <T extends RowData<string>>({
  addHighlightProps,
  answerTextClassName,
  dataDisplayType,
  getAnswerText,
  getRowId,
  groupAndAllNameMap,
  groups,
  groupsDiffs,
  hiddenRowIds,
  onGroupNameClick,
  rowsData,
  sortFunc,
  sortValue,
  t,
  tamRatio,
  totalData,
}: BuildIndividualTableInfoParams<T>): RowInfo<IndividualColumnKey>[] => {
  const mapGroup = <V>(
    mapFunc: (group: GroupAndAll, index: number) => V,
  ): Record<string, V> =>
    R.mapToObj(groups, (group, index) => [group, mapFunc(group, index)])

  const mapGroupsDiffEntries = <V>(
    mapFunc: (groupsDiff: GroupsDiff, index: number) => V,
  ): Record<GroupsDiffColumnKey, V> =>
    R.mapToObj(groupsDiffs, (groupsDiff, index) => [
      getDiffKey(groupsDiff),
      mapFunc(groupsDiff, index),
    ])

  const visibleRowsData = R.differenceWith(
    rowsData,
    hiddenRowIds,
    (rowData, hiddenRowId) => getRowId(rowData) === hiddenRowId,
  )

  const diffMinMaxMap = mapGroupsDiffEntries((groupsDiff) => {
    const ratioArray = rowsData.map(
      (rowData) => rowData.ratios[getDiffKey(groupsDiff)],
    )

    return { max: Math.max(...ratioArray), min: Math.min(...ratioArray) }
  })

  return compact([
    // ヘッダー
    {
      answerText: { isColumnHeader: true, rowSpan: 2 },
      ...mapGroup((group): CellInfo =>
        produce<CellInfo>(
          {
            align: 'center',
            icon: getSortIconBySortValue(sortValue, group),
            isBorderShownOnHoverWhenClickable: true,
            isColumnHeader: true,
            isHeader: true,
          },
          (draft) => {
            if (group === 'all') {
              draft.text = t(groupAllLabelI18nKey)
            } else {
              draft.group = group as Group
              draft.text = groupAndAllNameMap[group]
              draft.onClick = onGroupNameClick
                ? () => {
                    onGroupNameClick(group)
                  }
                : undefined
            }
          },
        ),
      ),
      ...mapGroupsDiffEntries((groupsDiff): CellInfo => ({
        align: 'center',
        icon: getSortIconBySortValue(sortValue, getDiffKey(groupsDiff)),
        isColumnHeader: true,
        isHeader: true,
        rowSpan: 2,
        text: getDiffText(groupsDiff, {
          textFn: (item) => groupAndAllNameMap[item],
        }),
      })),
    },
    // TOTAL
    mapGroup((group): CellInfo => {
      const count = totalData.counts[group]
      const ratio = totalData.ratios[group]

      return {
        align: 'center',
        isColumnHeader: true,
        text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
      }
    }),
    // 選択肢 / 顧客属性
    ...R.pipe(
      visibleRowsData,
      sortFunc,
      R.map((rowData): RowInfo<IndividualColumnKey> => ({
        answerText: {
          align: 'right',
          className: answerTextClassName,
          isHeader: true,
          text: getAnswerText(rowData),
          textCharacterLimit: 30,
        },
        ...mapGroup((group): CellInfo => {
          const ratio = rowData.ratios[group]
          const count = rowData.counts[group]

          const key = `${getRowId(rowData)}-${group}`

          return {
            align: 'center',
            ratio,
            text: NumFormat.forCount(count, ratio, tamRatio, dataDisplayType),
            ...addHighlightProps(key),
          }
        }),
        ...mapGroupsDiffEntries((groupsDiff): CellInfo => {
          const key = getDiffKey(groupsDiff)
          const ratio = rowData.ratios[key]
          const count = rowData.counts[key]
          const [text, copyValue] = NumFormat.countToPts(
            count,
            ratio,
            tamRatio,
            dataDisplayType,
          )
          const { max, min } = diffMinMaxMap[key]

          const diffKey = `diff-${getRowId(rowData)}-${key}`

          return {
            align: 'center',
            copyValue,
            heatmap: true,
            ratio: calcHeatmapRatio(ratio, max, min),
            text,
            ...addHighlightProps(diffKey),
          }
        }),
      })),
    ),
  ])
}
